import { useFetchFirstSessionExercises } from "@/infra/apis/useFetchFirstSessionExercises";
import { useCmsExerciseDetails } from "@/infra/hooks/useCmsExerciseDetails";
import { useIntl } from "@/infra/locale";
import type { DifficultyLevel } from "@akina/domain";
import type { SharedFormatMessage } from "@akina/infra";
import { ExerciseThumbnail, RoutineInformation } from "@akina/presentation";
import "swiper/css";
import "swiper/css/effect-fade";
import { Autoplay, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { RoutineTemplateResponse } from "types/schemas";

const DIFFICULTY_LEVELS = new Set<DifficultyLevel>(["easy", "medium", "hard"]);

type ExerciseSlideshowProps = {
  templateKey: string | null;
};

export function ExerciseSlideshow({ templateKey }: Readonly<ExerciseSlideshowProps>) {
  const { data, isLoading, isError } = useFetchFirstSessionExercises(templateKey ?? undefined);

  if (!templateKey || isError) {
    return (
      <div className="mx-auto w-[181px] shrink-0 lg:mx-0">
        <ExerciseCardSkeleton />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-[181px] shrink-0 lg:mx-0">
        <ExerciseCardSkeleton />
      </div>
    );
  }

  const exercises = data?.firstSession.routineTemplates ?? [];

  if (exercises.length === 0) {
    return <div className="mx-auto w-[181px] shrink-0 lg:mx-0" />;
  }

  return (
    <div className="mx-auto w-[181px] shrink-0 lg:mx-0">
      <Swiper
        modules={[Autoplay, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={800}
        autoplay={{ delay: 2000, disableOnInteraction: false }}
        loop
        slidesPerView={1}
        allowTouchMove={false}
        className="exercise-card-shadow rounded-xl">
        {exercises.map((exercise, i) => (
          <SwiperSlide key={exercise.routineTemplateKey ?? i}>
            <ExerciseCard exercise={exercise} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

function ExerciseCard({ exercise }: Readonly<{ exercise: RoutineTemplateResponse }>) {
  const { formatMessage } = useIntl();
  const details = useCmsExerciseDetails(exercise.exerciseTemplateKey);

  const isRepBased =
    exercise.routineMetric === "for_repetitions" || exercise.routineMetric === "as_many_repetitions_in_time";

  return (
    <div className="exercise-card-shadow grid h-[276px] w-full grid-rows-[144px_1fr] rounded-xl">
      <ExerciseThumbnail thumbnail={details?.imageUrl} previewGif={details?.previewGif} />
      <RoutineInformation
        exerciseName={details?.name ?? exercise.exerciseTemplateKey}
        difficulty={
          DIFFICULTY_LEVELS.has(exercise.difficultyLevel as DifficultyLevel)
            ? (exercise.difficultyLevel as DifficultyLevel)
            : null
        }
        routineSetAndBreaksInfo={{
          nSet: exercise.nSet ?? 0,
          nRepetitions: exercise.nRepetitions ?? undefined,
          duration: exercise.duration ?? undefined,
          breakDuration: exercise.breakDuration ?? "PT0S",
          isRepBased,
        }}
        formatMessage={formatMessage as unknown as SharedFormatMessage}
      />
    </div>
  );
}

function ExerciseCardSkeleton() {
  return (
    <div className="exercise-card-shadow grid h-[276px] w-full grid-rows-[144px_1fr] rounded-xl">
      <div className="h-full w-full animate-pulse rounded-t-xl bg-gray-200" />
      <div className="flex flex-col justify-center gap-1.5 rounded-b-xl bg-white p-3">
        <div className="h-3 w-10 animate-pulse rounded-full bg-gray-200" />
        <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
}
